import React, { useCallback, useRef, useState } from 'react'
import { Form } from 'react-bootstrap'
import Select from 'react-select'
import { useField, ErrorMessage } from 'formik'
import PropTypes from 'prop-types'
import classnames from 'classnames'

const normalizeOptions = (options) =>
  (options || []).map((opt) =>
    typeof opt === 'string' ? { value: opt, label: opt } : opt,
  )

const emitChange = (onChange, name, value) => {
  if (typeof onChange !== 'function') return
  onChange({
    target: { name, value: value ?? '' },
    currentTarget: { name, value: value ?? '' },
  })
}

// Fallbacks required: Bootstrap modals portal outside `.experience`, where
// tokens.scss variables are not defined (invalid var() → currentColor/black).
const DANGER = 'var(--stop, #c73939)'
const LINE = 'var(--line2, #cfccc4)'
const GOLD = 'var(--gold, #dfae3f)'

export const experienceSelectStyles = {
  control: (provided, state) => {
    const isInvalid =
      !!state.selectProps.isInvalid ||
      (typeof state.selectProps.className === 'string' &&
        state.selectProps.className.indexOf('is-invalid') !== -1)
    const borderColor = isInvalid ? DANGER : state.isFocused ? GOLD : LINE
    return {
      ...provided,
      minHeight: 38,
      borderRadius: 7,
      borderColor,
      borderWidth: 1,
      boxShadow: state.isFocused
        ? isInvalid
          ? '0 0 0 2px rgba(199, 57, 57, 0.2)'
          : '0 0 0 2px rgba(223, 174, 63, 0.25)'
        : 'none',
      backgroundColor: state.isDisabled
        ? 'var(--wash, #f6f5f2)'
        : 'var(--paper, #fff)',
      fontSize: 13.5,
      fontFamily: "var(--font-sans, 'Open Sans', sans-serif)",
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      opacity: 1,
      '&:hover': {
        borderColor,
      },
    }
  },
  valueContainer: (provided) => ({
    ...provided,
    padding: '2px 11px',
  }),
  singleValue: (provided, state) => ({
    ...provided,
    color: state.isDisabled ? 'var(--muted, #6e7178)' : 'var(--text, #1b1f26)',
    fontSize: 13.5,
    fontFamily: "var(--font-sans, 'Open Sans', sans-serif)",
    margin: 0,
  }),
  placeholder: (provided, state) => ({
    ...provided,
    color: state.isDisabled
      ? 'var(--muted, #6e7178)'
      : 'var(--muted2, #9a9da4)',
    fontSize: 13.5,
    fontFamily: "var(--font-sans, 'Open Sans', sans-serif)",
    margin: 0,
  }),
  input: (provided, state) => ({
    ...provided,
    margin: 0,
    fontSize: 13.5,
    fontFamily: "var(--font-sans, 'Open Sans', sans-serif)",
    color: 'var(--text, #1b1f26)',
    // Avoid a blinking "|" over the selected label (esp. disabled / closed menu).
    caretColor:
      state.isDisabled ||
      state.selectProps?.isDisabled ||
      !state.selectProps?.menuIsOpen
        ? 'transparent'
        : undefined,
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    paddingRight: 6,
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    color: 'var(--muted, #666A72)',
    padding: 6,
    transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'none',
    transition: 'transform 0.15s ease',
    '&:hover': {
      color: 'var(--muted, #666A72)',
    },
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  clearIndicator: (provided) => ({
    ...provided,
    color: 'var(--muted, #666A72)',
    padding: 4,
  }),
  menu: (provided) => ({
    ...provided,
    marginTop: provided.marginTop != null ? provided.marginTop : 4,
    marginBottom: provided.marginBottom != null ? provided.marginBottom : 4,
    borderRadius: 7,
    border: '1px solid var(--line, #e2e0da)',
    boxShadow: 'var(--shadow, 0 8px 24px rgba(27, 31, 38, 0.12))',
    backgroundColor: 'var(--paper, #fff)',
    overflow: 'hidden',
    zIndex: 20,
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 1060,
  }),
  menuList: (provided) => ({
    ...provided,
    padding: 4,
  }),
  option: (provided, state) => ({
    ...provided,
    borderRadius: 5,
    padding: '8px 10px',
    fontSize: 13,
    fontFamily: "var(--font-sans, 'Open Sans', sans-serif)",
    color: state.isDisabled ? 'var(--muted2, #9a9da4)' : 'var(--text, #1b1f26)',
    cursor: state.isDisabled ? 'not-allowed' : 'pointer',
    backgroundColor: state.isDisabled
      ? 'transparent'
      : state.isSelected
      ? 'rgba(223, 174, 63, 0.22)'
      : state.isFocused
      ? 'rgba(223, 174, 63, 0.12)'
      : 'transparent',
    ':active': {
      backgroundColor: state.isDisabled
        ? 'transparent'
        : 'rgba(223, 174, 63, 0.22)',
    },
  }),
  noOptionsMessage: (provided) => ({
    ...provided,
    fontSize: 13,
    fontFamily: "var(--font-sans, 'Open Sans', sans-serif)",
    color: 'var(--muted, #6e7178)',
  }),
}

/**
 * react-select v3 `menuPlacement="auto"` breaks with menuPosition="fixed" +
 * portal (offsetParent is null → always bottoms). Measure viewport space
 * ourselves and choose top/bottom before the menu opens.
 */
const measureMenuPlacement = (wrapEl, minHeight) => {
  if (!wrapEl || typeof window === 'undefined') return 'bottom'
  const control =
    wrapEl.querySelector('.experience-select__control') ||
    wrapEl.querySelector('[class*="-control"]')
  if (!control) return 'bottom'

  const rect = control.getBoundingClientRect()
  // Leave room for sticky experience footer / page chrome.
  const footerBuffer = 72
  const spaceBelow = window.innerHeight - rect.bottom - footerBuffer
  const spaceAbove = rect.top
  const needed = minHeight || 200

  if (spaceBelow >= needed) return 'bottom'
  if (spaceAbove > spaceBelow) return 'top'
  return 'bottom'
}

const ExperienceSelectControl = ({
  id,
  name,
  label,
  options,
  placeholder,
  disabled,
  onBlur,
  className,
  value,
  onChange,
  isInvalid,
  isSearchable,
  isClearable = false,
  isOptionDisabled,
  menuPlacement = 'auto',
  maxMenuHeight = 260,
  minMenuHeight = 200,
  errorNode = null,
}) => {
  const wrapRef = useRef(null)
  const [autoPlacement, setAutoPlacement] = useState('bottom')
  const [menuIsOpen, setMenuIsOpen] = useState(false)
  const isAuto = menuPlacement === 'auto'
  const normalized = normalizeOptions(options)
  const selected =
    normalized.find((opt) => String(opt.value) === String(value ?? '')) || null

  const resolvedPlacement = isAuto ? autoPlacement : menuPlacement

  const handleMenuOpen = useCallback(() => {
    if (isAuto) {
      setAutoPlacement(measureMenuPlacement(wrapRef.current, minMenuHeight))
      setMenuIsOpen(true)
    }
  }, [isAuto, minMenuHeight])

  const handleMenuClose = useCallback(() => {
    if (isAuto) {
      setMenuIsOpen(false)
    }
  }, [isAuto])

  return (
    <div ref={wrapRef} className={classnames('experience-f', className)}>
      {label ? (
        <Form.Label htmlFor={id} className='experience-f-label'>
          {label}
        </Form.Label>
      ) : null}
      <Select
        inputId={id}
        name={name}
        classNamePrefix='experience-select'
        className={classnames('experience-select-control', {
          'is-invalid': isInvalid,
        })}
        isInvalid={!!isInvalid}
        options={normalized}
        value={selected}
        placeholder={placeholder || 'Select…'}
        isDisabled={disabled}
        isSearchable={disabled ? false : isSearchable}
        isClearable={isClearable && !disabled}
        isOptionDisabled={isOptionDisabled}
        menuPlacement={resolvedPlacement}
        maxMenuHeight={maxMenuHeight}
        minMenuHeight={minMenuHeight}
        menuPosition='fixed'
        menuPortalTarget={
          typeof document !== 'undefined' ? document.body : null
        }
        menuIsOpen={isAuto ? menuIsOpen : undefined}
        onMenuOpen={handleMenuOpen}
        onMenuClose={handleMenuClose}
        styles={experienceSelectStyles}
        onChange={(option) => {
          emitChange(onChange, name, option?.value)
          if (isAuto) {
            setMenuIsOpen(false)
          }
        }}
        onBlur={onBlur}
      />
      {errorNode}
    </div>
  )
}

const ExperienceSelectControlled = (props) => (
  <ExperienceSelectControl id={props.id || props.name} {...props} />
)

const ExperienceSelectFormik = ({
  id,
  name,
  label,
  options,
  placeholder,
  disabled,
  onBlur,
  className,
  size,
  value: valueOverride,
  onChange: onChangeOverride,
  isSearchable,
  showErrorMessage,
  isOptionDisabled,
  menuPlacement,
  maxMenuHeight,
  minMenuHeight,
}) => {
  const [field, meta, helpers] = useField(name)
  const value = valueOverride !== undefined ? valueOverride : field.value ?? ''
  const isInvalid =
    (meta.touched && !!meta.error) || (showErrorMessage && !!meta.error)

  const handleChange = (e) => {
    if (onChangeOverride) {
      onChangeOverride(e)
    } else {
      helpers.setValue(e.target.value)
    }
  }

  const handleBlur = (e) => {
    helpers.setTouched(true, true)
    if (onBlur) {
      onBlur(e)
    }
  }

  const showError =
    (meta.touched && !!meta.error) || (showErrorMessage && !!meta.error)

  return (
    <ExperienceSelectControl
      id={id || name}
      name={name}
      label={label}
      options={options}
      placeholder={placeholder}
      disabled={disabled}
      onBlur={handleBlur}
      className={className}
      size={size}
      value={value}
      onChange={handleChange}
      isInvalid={isInvalid}
      isSearchable={isSearchable}
      isOptionDisabled={isOptionDisabled}
      menuPlacement={menuPlacement}
      maxMenuHeight={maxMenuHeight}
      minMenuHeight={minMenuHeight}
      errorNode={
        showError ? (
          <Form.Text className='experience-f-error' role='alert'>
            <ErrorMessage component='span' name={name} />
          </Form.Text>
        ) : null
      }
    />
  )
}

const ExperienceSelect = (props) => {
  // When `name` is set, bind to Formik so custom onChange (e.g. autosave)
  // still keeps the selected value visible in the control.
  if (props.name) {
    return <ExperienceSelectFormik {...props} />
  }

  return <ExperienceSelectControlled {...props} />
}

ExperienceSelect.propTypes = {
  name: PropTypes.string,
  label: PropTypes.node,
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          .isRequired,
        label: PropTypes.node.isRequired,
      }),
    ]),
  ),
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  onBlur: PropTypes.func,
  className: PropTypes.string,
  showErrorMessage: PropTypes.bool,
  size: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  id: PropTypes.string,
  isSearchable: PropTypes.bool,
  isClearable: PropTypes.bool,
  isInvalid: PropTypes.bool,
  isOptionDisabled: PropTypes.func,
  menuPlacement: PropTypes.oneOf(['auto', 'bottom', 'top']),
  maxMenuHeight: PropTypes.number,
  minMenuHeight: PropTypes.number,
}

ExperienceSelect.defaultProps = {
  options: [],
  showErrorMessage: false,
  isSearchable: true,
  menuPlacement: 'auto',
  maxMenuHeight: 260,
  minMenuHeight: 200,
}

export default ExperienceSelect
